import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { isWebMcpTestHost, registerWebMcpTools } from "lib/webmcp/register";

const CHAT_FIELD_LABELS = [
  "이름",
  "티커",
  "리스크 수준",
  "예상 CVaR",
  "가격 변동률",
  "현재 가격",
  "요약",
];

/** 모델이 리스트를 한 줄로 붙인 경우 라벨마다 줄바꿈 */
function normalizeAssistantMarkdown(text: string) {
  let s = text.replace(/\r\n/g, "\n").trim();
  s = s.replace(/\s+-\s+\*\*/g, "\n- **");
  s = s.replace(/:\s+-\s+/g, ":\n- ");
  s = s.replace(/\s+\*\*(요약|정리|참고)\*\*\s*:/g, "\n\n**$1**:");
  const labelAlt = CHAT_FIELD_LABELS.map((l) => l.replace(/\s+/g, "\\s+")).join("|");
  s = s.replace(new RegExp(`\\s+-\\s+(${labelAlt})\\s*:`, "g"), "\n- $1:");
  return s.trim();
}

function ChatMarkdown({ text }: { text: string }) {
  return (
    <div className="webmcp-chat-md [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown
        components={{
          p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
          ul: ({ children }) => (
            <ul className="mb-2 list-disc space-y-1 pl-4 last:mb-0">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="mb-2 list-decimal space-y-1 pl-4 last:mb-0">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          a: ({ href, children }) => (
            <a href={href} className="underline" target="_blank" rel="noreferrer">
              {children}
            </a>
          ),
        }}
      >
        {normalizeAssistantMarkdown(text)}
      </ReactMarkdown>
    </div>
  );
}

type ChatMsg = {
  role: "user" | "assistant";
  content?: string;
};

type SourceItem = {
  tool?: string;
  found?: boolean | null;
  ticker?: string | null;
  name_kr?: string | null;
  as_of?: string | null;
};

function WebMcpHost() {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [debug, setDebug] = useState(false);
  const [api, setApi] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([
    {
      role: "assistant",
      content:
        "종목 리스크, 뉴스, 투자매력도, 지수, 핫토픽을 RiskWeather DB에서 찾아 드릴게요. 예: 삼성전자 리스크 알려줘",
    },
  ]);
  const [sources, setSources] = useState<SourceItem[]>([]);
  const [lastMeta, setLastMeta] = useState<any>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isWebMcpTestHost()) return;
    setEnabled(true);
    const ac = new AbortController();
    registerWebMcpTools(ac.signal).then((r) => setApi(r.api));
    return () => ac.abort();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  if (!enabled) return null;

  const visible = messages.filter((m) => Boolean(m.content));

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    const next: ChatMsg[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setBusy(true);
    setSources([]);
    try {
      const res = await fetch("/api/webmcp/chat", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          messages: next.map((m) => ({
            role: m.role,
            content: m.content ?? "",
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data?.error || "요청에 실패했습니다." },
        ]);
        return;
      }
      const content = data?.message?.content || "답변을 만들지 못했습니다.";
      setMessages([...next, { role: "assistant", content }]);
      setSources(Array.isArray(data?.sources) ? data.sources : []);
      setLastMeta({ rounds: data?.rounds, navigate: data?.navigate, sources: data?.sources });
      if (data?.navigate && typeof data.navigate === "string") {
        window.location.href = data.navigate;
      }
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: err?.message || "오류가 발생했습니다." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[400] text-left">
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-full bg-gray80 px-4 py-3 text-sm text-white shadow-lg"
        >
          리스크 물어보기
        </button>
      )}
      {open && (
        <div className="flex h-[520px] w-[380px] max-h-[80vh] flex-col overflow-hidden rounded-2xl border border-gray20 bg-white shadow-xl">
          <div className="flex items-center justify-between bg-gray80 px-3 py-2 text-white">
            <div>
              <p className="text-sm font-semibold">RiskWeather 도우미</p>
              <p className="text-[11px] text-gray20">
                test 전용 · {api ? "WebMCP 연결됨" : "사이트 챗봇"}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="text-[11px] underline"
                onClick={() => setDebug((v) => !v)}
              >
                {debug ? "채팅" : "로그"}
              </button>
              <button type="button" onClick={() => setOpen(false)}>
                ✕
              </button>
            </div>
          </div>
          <div className="flex-1 space-y-2 overflow-auto p-3 text-sm">
            {visible.map((m, i) => (
              <div
                key={i}
                className={`rounded-lg px-3 py-2 ${
                  m.role === "user" ? "ml-8 bg-blue10 text-gray80" : "mr-8 bg-gray10 text-gray80"
                }`}
              >
                {m.role === "assistant" ? (
                  <ChatMarkdown text={m.content || ""} />
                ) : (
                  <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
                )}
              </div>
            ))}
            {busy && <p className="text-xs text-gray50">DB에서 찾는 중...</p>}
            {!busy && sources.length > 0 && (
              <p className="text-[11px] text-gray50">
                출처:{" "}
                {sources
                  .map((s) =>
                    [s.tool, s.name_kr || s.ticker, s.as_of].filter(Boolean).join(" ")
                  )
                  .join(" · ")}
              </p>
            )}
            {debug && (
              <pre className="whitespace-pre-wrap rounded bg-gray10 p-2 text-[10px] text-gray50">
                {JSON.stringify(lastMeta, null, 2)}
              </pre>
            )}
            <div ref={bottomRef} />
          </div>
          <form
            className="flex gap-2 border-t border-gray20 p-2"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <input
              className="flex-1 rounded-lg border border-gray20 px-3 py-2 text-sm"
              placeholder="삼성전자 리스크 알려줘"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={busy}
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="rounded-lg bg-blue50 px-3 py-2 text-sm text-white disabled:opacity-40"
            >
              보내기
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default WebMcpHost;
